import React, { useState } from "react";
import {
  faEnvelope,
  faLocationDot,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Helmet } from "react-helmet";
import CenterLoading from "../components/core/CenterLoading";
import { getAutocompleteType } from "../components/contact/ContactForm";
import MarketingHero from "components/marketing/MarketingHero";
import users from "services/users";

type InputState = {
  name: string;
  phone: string;
  email: string;
  message: string;
};

const defaultInputState: InputState = {
  name: "",
  phone: "",
  email: "",
  message: "",
};

const Contact = () => {
  const [inputs, setInputs] = useState<InputState>(defaultInputState);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "idle" | "success" | "error";
    message: string;
  }>({
    type: "idle",
    message: "",
  });

  const handleChange =
    (fieldName: keyof InputState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setInputs((current) => ({
        ...current,
        [fieldName]: event.target.value,
      }));
      setStatusMessage({
        type: "idle",
        message: "",
      });
    };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setStatusMessage({
      type: "idle",
      message: "",
    });

    try {
      await users.contactUs({
        fullName: inputs.name,
        contactNumber: inputs.phone,
        emailAddress: inputs.email,
        message: inputs.message,
      });

      setInputs(defaultInputState);
      setStatusMessage({
        type: "success",
        message: "Message sent. We'll get back to you shortly.",
      });
    } catch (error) {
      console.log(error);
      setStatusMessage({
        type: "error",
        message:
          "We couldn't send your message right now. Please try again shortly.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="marketing-page marketing-page--contact">
      <Helmet>
        <title>Contact Us | insite.work</title>
        <meta
          name="description"
          content="Contact insite.work using the form below or directly through our team."
        />
      </Helmet>

      <CenterLoading isVisible={isLoading} />

      <main className="marketing-page__main">
        <MarketingHero title="Contact Us" />

        <section className="marketing-page__section">
          <div className="marketing-page__section-inner">
            <p className="marketing-page__intro">
              Contact insite.work using the form below or directly via our
              details.
            </p>

            <div className="marketing-page__content-grid">
              <div className="marketing-page__form-panel">
                <form className="marketing-page__form" onSubmit={onSubmit}>
                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="contact-name">
                      Full Name
                    </label>
                    <input
                      id="contact-name"
                      className="marketing-page__input"
                      type="text"
                      required
                      autoComplete={getAutocompleteType("fullName")}
                      value={inputs.name}
                      onChange={handleChange("name")}
                    />
                  </div>

                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="contact-phone">
                      Contact Number
                    </label>
                    <input
                      id="contact-phone"
                      className="marketing-page__input"
                      type="text"
                      autoComplete={getAutocompleteType("phoneNumber")}
                      value={inputs.phone}
                      onChange={handleChange("phone")}
                    />
                  </div>

                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="contact-email">
                      Email Address
                    </label>
                    <input
                      id="contact-email"
                      className="marketing-page__input"
                      type="email"
                      required
                      autoComplete={getAutocompleteType("email")}
                      value={inputs.email}
                      onChange={handleChange("email")}
                    />
                  </div>

                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="contact-message">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      className="marketing-page__textarea"
                      rows={6}
                      required
                      value={inputs.message}
                      onChange={handleChange("message")}
                    />
                  </div>

                  {statusMessage.type !== "idle" && (
                    <div
                      role={statusMessage.type === "error" ? "alert" : "status"}
                      aria-live="polite"
                      className={`marketing-page__feedback marketing-page__feedback--${statusMessage.type}`}
                    >
                      {statusMessage.message}
                    </div>
                  )}

                  <button
                    title="send"
                    aria-label="Send"
                    type="submit"
                    disabled={isLoading}
                    className="marketing__button marketing__button--primary marketing-page__full-button"
                  >
                    {isLoading ? "Sending..." : "Send"}
                  </button>
                </form>
              </div>

              <aside className="marketing-page__sidebar">
                <h2 className="marketing-page__sidebar-title">
                  Get in touch directly
                </h2>

                <div className="marketing-page__sidebar-items">
                  <div className="marketing-page__sidebar-item">
                    <div className="marketing-page__sidebar-icon">
                      <FontAwesomeIcon icon={faLocationDot} />
                    </div>
                    <p>
                      Insite.Work
                      <br />
                      PO Box 68
                      <br />
                      Wilston, Brisbane
                      <br />
                      4051
                    </p>
                  </div>

                  <div className="marketing-page__sidebar-item">
                    <div className="marketing-page__sidebar-icon">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </div>
                    <a href="mailto:admin@insite.work">admin@insite.work</a>
                  </div>

                  <div className="marketing-page__sidebar-item">
                    <div className="marketing-page__sidebar-icon">
                      <FontAwesomeIcon icon={faPhone} />
                    </div>
                    <a href="tel:+61731023496">(07) 3102 3496</a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Contact;
